import { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Gamepad2, Mail, Lock, User, Loader2, Gift, Zap, Trophy } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useContext(UserContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, { name, email, password });
      login(response.data.user, response.data.token);
      toast.success("Welcome! ₹1,000 bonus added to your wallet!");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0F0F10] flex">
      {/* Left - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1596838132731-3301c3fd4317?w=1200&h=1200&fit=crop"
          alt="Casino Slots"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0F0F10]" />
        <div className="absolute inset-0 bg-black/40" />
        
        {/* Overlay Content */}
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center">
            <div className="w-20 h-20 rounded-2xl bg-[#FFD600] flex items-center justify-center mx-auto mb-6 shadow-[0_0_40px_rgba(255,214,0,0.5)]">
              <Gift className="w-10 h-10 text-black" />
            </div>
            <h2 className="text-5xl font-bold text-white font-['Rajdhani'] mb-4">
              ₹1,000<br />
              <span className="text-[#FFD600]">FREE BONUS</span>
            </h2>
            <p className="text-white/60 text-lg">No deposit required</p>
          </div>
        </div>
      </div>
      
      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-[#00E701] flex items-center justify-center shadow-[0_0_30px_rgba(0,231,1,0.5)]">
              <Gamepad2 className="w-7 h-7 text-black" />
            </div>
            <span className="text-2xl font-bold text-white font-['Rajdhani']">BetIndia</span>
          </Link>

          {/* Bonus Banner */}
          <div className="mb-8 p-5 rounded-2xl bg-gradient-to-r from-[#FFD600]/20 to-[#FF6B00]/10 border border-[#FFD600]/30">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#FFD600] flex items-center justify-center shrink-0">
                <Trophy className="w-6 h-6 text-black" />
              </div>
              <div>
                <p className="text-[#FFD600] font-bold text-lg">Welcome Bonus</p>
                <p className="text-white/60 text-sm">Get ₹1,000 FREE on signup!</p>
              </div>
            </div>
          </div>

          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-white font-['Rajdhani'] mb-2">
              Create Account
            </h1>
            <p className="text-white/50">Join thousands of winners</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full Name"
                className="pl-12 h-14 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl text-base"
                required
                data-testid="register-name-input"
              />
            </div>

            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="pl-12 h-14 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl text-base"
                required
                data-testid="register-email-input"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password (min 6 characters)"
                className="pl-12 h-14 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl text-base"
                minLength={6}
                required
                data-testid="register-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-[#00E701] hover:bg-[#00C201] text-black font-bold text-lg rounded-xl shadow-[0_0_30px_rgba(0,231,1,0.4)]"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  <Zap className="w-5 h-5 mr-2" />
                  Claim ₹1,000 Bonus
                </>
              )}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/30 text-sm">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <Button
            type="button"
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full h-14 border-white/10 text-white hover:bg-white/5 rounded-xl text-base"
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="text-center text-white/50 mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-[#00E701] hover:underline font-medium">
              Sign in
            </Link>
          </p>

          <p className="text-center text-white/30 text-xs mt-4">
            By signing up, you confirm you are 18+ and agree to play responsibly
          </p>
        </div>
      </div>
    </div>
  );
};
